import React, { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import {
  ExternalLink,
  ArrowRight,
  Check,
  DollarSign,
  Loader,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import API_CONFIG from "../../lib/ApiConfig";

const RefinancingHandoff = ({ vehicleData, onComplete }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [error, setError] = useState(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [rewardCount, setRewardCount] = useState(0);

  const { getIdToken } = useAuth();

  const handleSubmitToServer = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Get authentication token
      const token = await getIdToken();
      if (!token) {
        throw new Error("Authentication required");
      }

      // Prepare the payload to match your DTO structure
      const payload = {
        vin: vehicleData.vin || null,
        year: vehicleData.year || null,
        make: vehicleData.make || null,
        model: vehicleData.model || null,
        mileage: vehicleData.mileage || null,
        state: vehicleData.state || null,
        license: vehicleData.license || null,
      };

      // Submit to your backend
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/refinancing/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-token": token,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `Server error: ${response.status}`
        );
      }

      const responseData = await response.json();

      // Store the response data
      setRedirectUrl(responseData.redirectUrl);
      setRewardCount(responseData.rewardCount || 0);
      setSubmissionComplete(true);

      // Store submission data for analytics/tracking
      sessionStorage.setItem(
        "refinancingSubmission",
        JSON.stringify({
          flowId: responseData.flowId,
          vehicleCount: responseData.vehicleCount,
          rewardCount: responseData.rewardCount,
          submittedAt: new Date().toISOString(),
          redirectUrl: responseData.redirectUrl,
        })
      );
      console.log("Refinancing submission successful:", responseData);
      return responseData; // Return the data for handleRedirect to use

    } catch (err) {
      console.error("Error submitting refinancing data:", err);
      setError(
        err.message || "Failed to submit vehicle data. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRedirect = async () => {
    if (isRedirecting) return;

    // If we haven't submitted yet, submit first then redirect
    if (!submissionComplete) {
      try {
        await handleSubmitToServer();
        // After successful submission, the state will be updated and we'll have redirectUrl
        // Now we can proceed with redirect using the fresh state
      } catch (error) {
        // Error already handled in handleSubmitToServer
        return;
      }
    }

    // At this point we should have a redirectUrl (either from previous submission or just completed)
    // Get the latest data from either state or sessionStorage as fallback
    const finalRedirectUrl =
      redirectUrl ||
      (sessionStorage.getItem("refinancingSubmission")
        ? JSON.parse(sessionStorage.getItem("refinancingSubmission"))
            .redirectUrl
        : null);

    if (!finalRedirectUrl) {
      console.error("No redirect URL available");
      return;
    }

    setIsRedirecting(true);

    console.log("Redirecting to Refinancing Partner:", finalRedirectUrl);

    // Store handoff data for analytics/tracking
    sessionStorage.setItem(
      "refinancingHandoff",
      JSON.stringify({
        vehicleData,
        handoffAt: new Date().toISOString(),
        partnerUrl: finalRedirectUrl,
        flowId: sessionStorage.getItem("refinancingSubmission")
          ? JSON.parse(sessionStorage.getItem("refinancingSubmission"))
              .flowId
          : null,
        rewardCount: rewardCount,
      })
    );

    // Redirect to the URL provided by the server
    window.location.href = finalRedirectUrl;
  };

  const formatVehicleInfo = () => {
    const parts = [];
    if (vehicleData.year) parts.push(vehicleData.year);
    if (vehicleData.make) parts.push(vehicleData.make);
    if (vehicleData.model) parts.push(vehicleData.model);
    return parts.join(" ") || "Your vehicle";
  };

  // Show loading state while submitting
  if (isSubmitting) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <Loader className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Processing Your Information
          </h1>
          <p className="text-gray-600">
            We're saving your vehicle information and preparing your refinancing quotes...
          </p>
        </div>

        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div>
                <p className="font-medium text-blue-900">
                  Processing Vehicle Information
                </p>
                <p className="text-sm text-blue-700">
                  {formatVehicleInfo()}
                  {vehicleData.mileage &&
                    ` • ${vehicleData.mileage.toLocaleString()} miles`}
                  {vehicleData.state && ` • ${vehicleData.state}`}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state if submission failed
  if (error) {
    return (
      <div className="space-y-6 p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Something Went Wrong
          </h1>
          <p className="text-gray-600">
            We encountered an issue processing your information.
          </p>
        </div>

        <Card className="bg-red-50 border-red-100">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 mt-1" />
              <div>
                <p className="font-medium text-red-900">Error Details</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <button
          onClick={handleSubmitToServer}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <span>Try Again</span>
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  // Show success state with redirect option (only after successful submission)
  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-green-500" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">You're All Set!</h1>
        <p className="text-gray-600">
          Your vehicle information has been saved
          {rewardCount > 0 &&
            ` and you've earned ${rewardCount} reward${
              rewardCount !== 1 ? "s" : ""
            }!`}{" "}
          We're connecting you with our refinancing partner to get you personalized rates and potential savings.
        </p>
      </div>

      {/* Vehicle Summary */}
      <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div>
              <p className="text-sm text-blue-700">
                {formatVehicleInfo()}
                {vehicleData.mileage &&
                  ` • ${vehicleData.mileage.toLocaleString()} miles`}
                {vehicleData.state && ` • ${vehicleData.state}`}
              </p>
              {vehicleData.vin && (
                <p className="text-xs text-blue-600 font-mono mt-1">
                  VIN: {vehicleData.vin}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reward notification if applicable */}
      {rewardCount > 0 && (
        <Card className="bg-green-50 border-green-100">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <Check className="w-5 h-5 text-green-500 mt-1" />
              <div>
                <p className="font-medium text-green-900">
                  Reward{rewardCount !== 1 ? "s" : ""} Earned!
                </p>
                <p className="text-sm text-green-700">
                  You've earned {rewardCount} reward
                  {rewardCount !== 1 ? "s" : ""} for sharing your vehicle
                  information. Check your Ownli wallet to see your rewards.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Redirect Button */}
      <Card className="border border-green-100">
        <CardContent className="pt-6">
          <div className="text-center space-y-4">
            <button
              onClick={handleRedirect}
              disabled={isSubmitting || isRedirecting}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors font-medium mt-4 ${
                isSubmitting || isRedirecting
                  ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Saving Vehicle Data...</span>
                </>
              ) : isRedirecting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  <span>Redirecting...</span>
                </>
              ) : (
                <>
                  <span>Get My Refinancing Quotes</span>
                  <ExternalLink className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* What to expect */}
      <Card>
        <CardContent className="pt-6">
          <h3 className="font-medium text-gray-900 mb-3">
            What to Expect with Refinancing
          </h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-green-700">1</span>
              </div>
              <span>Review your pre-filled vehicle information</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-green-700">2</span>
              </div>
              <span>See personalized refinancing rates</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-green-700">3</span>
              </div>
              <span>Compare loan terms and potential savings</span>
            </li>
            <li className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs text-green-700">4</span>
              </div>
              <span>Choose the best refinancing option</span>
            </li>
          </ul>
        </CardContent>
      </Card>

      {/* Footer */}
      <p className="text-xs text-center text-gray-500">
        Thank you for using Ownli. You'll be redirected to our partner to
        complete your refinancing quote.
      </p>
    </div>
  );
};

export default RefinancingHandoff;